'use client';

import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState
} from 'react';

import { hasHandoverSessionCookie, setHandoverSessionCookie } from './HandoverSession';
import { getLongestLeaseTermId } from './utils';

const LEASE_TERM_STORAGE_KEY = 'cb-bjou-current-lease-term';

export type LeaseTerm = {
  id: string;
  duration: number;
  monthlyAmount: number;
};

type LeaseContextType = {
  customerId: string;
  leaseTerms: LeaseTerm[] | null;
  selectedTermId: string;
  setSelectedTermId: Dispatch<SetStateAction<string>>;
  selectedLeaseTerm: number | null;
  selectedLeaseAmount?: number | null;
  legacyLeaseAmount?: number | null;
  isLegacyLeaseFlow?: boolean;
};

const initialState: LeaseContextType = {
  customerId: '',
  leaseTerms: null,
  selectedTermId: '',
  setSelectedTermId: () => {},
  selectedLeaseTerm: null,
  selectedLeaseAmount: null,
  legacyLeaseAmount: null,
  isLegacyLeaseFlow: false
};

const LeaseContext = createContext<LeaseContextType>(initialState);

type LeaseProviderProps = {
  children: ReactNode;
  customerId: string;
  leaseTerms: LeaseTerm[] | null;
  legacyLeaseAmount?: number | null;
  isLegacyLeaseFlow?: boolean;
  isBusinessHandover?: boolean;
};

const LeaseProvider = ({
  children,
  customerId,
  leaseTerms,
  legacyLeaseAmount,
  isLegacyLeaseFlow = false,
  isBusinessHandover = false
}: LeaseProviderProps) => {
  const [selectedTermId, setSelectedTermId] = useState<string>('');

  useEffect(() => {
    const storedTermId = localStorage.getItem(LEASE_TERM_STORAGE_KEY);
    if (storedTermId) {
      setSelectedTermId(storedTermId);
    } else {
      const defaultTermId = getLongestLeaseTermId(leaseTerms);
      if (defaultTermId) {
        setSelectedTermId(defaultTermId);
        localStorage.setItem(LEASE_TERM_STORAGE_KEY, defaultTermId);
      }
    }
  }, [leaseTerms]);

  useEffect(() => {
    if (selectedTermId) {
      localStorage.setItem(LEASE_TERM_STORAGE_KEY, selectedTermId);
    }
  }, [selectedTermId]);

  const selectedLeaseAmount =
    leaseTerms?.find(term => term.id === selectedTermId)?.monthlyAmount || null;

  const selectedLeaseTerm = leaseTerms?.find(term => term.id === selectedTermId)?.duration || null;

  useEffect(() => {
    if (isBusinessHandover) {
      if (hasHandoverSessionCookie()) {
        return;
      }
      setHandoverSessionCookie();
    }
  }, [isBusinessHandover]);

  return (
    <LeaseContext.Provider
      value={{
        customerId,
        leaseTerms,
        selectedTermId,
        setSelectedTermId,
        selectedLeaseTerm,
        selectedLeaseAmount,
        legacyLeaseAmount,
        isLegacyLeaseFlow
      }}
    >
      {children}
    </LeaseContext.Provider>
  );
};

const useLease = () => {
  const context = useContext(LeaseContext);
  if (context === undefined) {
    throw new Error('useLease must be used within a LeaseProvider');
  }
  return context;
};

export { LeaseContext, LeaseProvider, useLease };
